import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Save, MapPin, Image, Upload, X, Plus } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import ApiService from '../services/api';
import cloudinaryService from '../services/cloudinary';
import toast from 'react-hot-toast';

const AutoResizeTextarea = ({ name, value, onChange, placeholder, rows = 3, className, required }) => {
  const textareaRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [value]);

  return (
    <textarea
      ref={textareaRef}
      name={name}
      value={value}
      onChange={onChange}
      rows={rows}
      required={required}
      className={`${className} overflow-hidden resize-none`}
      placeholder={placeholder}
    />
  );
};

const AddEditTirthSthal = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    imageUrl: '',
    videoUrl: '',
    address: '',
    city: '',
    state: '',
    country: 'India',
    latitude: '',
    longitude: '',
    category: 'Temple',
    deity: '',
    significance: '',
    bestTimeToVisit: '',
    darshantimings: '',
    entryFee: '',
    nearbyPlaces: [],
    history: '',
    festivals: [],
    rituals: [],
    facilities: [],
    howToReach: '',
    nearestAirport: '',
    nearestRailway: '',
    galleryImages: [],
    isActive: true,
    isFeatured: false,
    priority: 0
  });

  const [tempInput, setTempInput] = useState({
    nearbyPlace: '',
    festival: '',
    ritual: '',
    facility: ''
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const autocompleteInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  const categories = ['Temple', 'River', 'Mountain', 'Sacred Grove', 'Historical Site', 'Ashram', 'Other'];

  // Google Maps API Key from environment variable
  const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

  // Load Google Maps script
  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY) {
      console.warn('Google Maps API key not configured. Set VITE_GOOGLE_MAPS_API_KEY in your .env file');
      return;
    }

    // Check if script is already loaded
    if (window.google && window.google.maps) {
      initializeAutocomplete();
      return;
    }

    // Load Google Maps script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      initializeAutocomplete();
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup if needed
    };
  }, [GOOGLE_MAPS_API_KEY]);

  const initializeAutocomplete = () => {
    if (!autocompleteInputRef.current || !window.google) return;

    const autocomplete = new window.google.maps.places.Autocomplete(
      autocompleteInputRef.current,
      {
        types: ['establishment', 'geocode'],
        fields: ['name', 'formatted_address', 'geometry', 'address_components', 'place_id']
      }
    );

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      
      if (!place.geometry) {
        toast.error('No details available for this place');
        return;
      }

      handlePlaceSelect(place);
    });

    autocompleteRef.current = autocomplete;
  };

  useEffect(() => {
    if (isEditMode) {
      fetchTirthSthalDetails();
    }
  }, [id]);

  const fetchTirthSthalDetails = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getTirthSthalById(id);
      if (response.success) {
        setFormData({
          ...response.data,
          latitude: response.data.latitude?.toString() || '',
          longitude: response.data.longitude?.toString() || ''
        });
      }
    } catch (error) {
      console.error('Error fetching Tirth Sthal:', error);
      toast.error('Failed to fetch Tirth Sthal details');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle place selection from Google Places
  const handlePlaceSelect = (place) => {
    try {
      const location = place.geometry.location;
      const addressComponents = place.address_components || [];
      
      // Extract city, state, country from address components
      let city = '';
      let state = '';
      let country = 'India';
      
      addressComponents.forEach(component => {
        if (component.types.includes('locality')) {
          city = component.long_name;
        } else if (component.types.includes('administrative_area_level_2') && !city) {
          city = component.long_name;
        }
        if (component.types.includes('administrative_area_level_1')) {
          state = component.long_name;
        }
        if (component.types.includes('country')) {
          country = component.long_name;
        }
      });
      
      setFormData(prev => ({
        ...prev,
        name: prev.name || place.name || '',
        address: place.formatted_address || '',
        city: city || prev.city,
        state: state || prev.state,
        country: country || prev.country,
        latitude: location.lat().toString(),
        longitude: location.lng().toString(),
      }));

      toast.success('Location details filled successfully');
    } catch (error) {
      console.error('Error processing place details:', error);
      toast.error('Failed to process place details');
    }
  };

  const handleImageUpload = async (e, fieldName = 'imageUrl') => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file
    const validation = cloudinaryService.validateFile(file, {
      maxSize: 5 * 1024 * 1024, // 5MB
      allowedTypes: ['.jpg', '.jpeg', '.png', '.gif', '.webp']
    });

    if (!validation.isValid) {
      toast.error(validation.errors.join(', '));
      return;
    }

    try {
      setUploading(true);
      
      // Upload to Cloudinary
      const result = await cloudinaryService.uploadFile(file, {
        folder: 'vaidic/tirth-sthals',
        resourceType: 'image',
        tags: ['tirth-sthal', 'pilgrimage']
      });

      if (result.success) {
        if (fieldName === 'galleryImages') {
          setFormData(prev => ({
            ...prev,
            galleryImages: [...prev.galleryImages, result.url]
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            [fieldName]: result.url
          }));
        }
        toast.success('Image uploaded successfully');
      } else {
        toast.error(result.error || 'Failed to upload image');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleAddArrayItem = (fieldName, inputKey) => {
    const value = tempInput[inputKey]?.trim();
    if (value) {
      setFormData(prev => ({
        ...prev,
        [fieldName]: [...prev[fieldName], value]
      }));
      setTempInput(prev => ({ ...prev, [inputKey]: '' }));
    }
  };

  const handleRemoveArrayItem = (fieldName, index) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: prev[fieldName].filter((_, i) => i !== index)
    }));
  };

  const handleRemoveGalleryImage = (index) => {
    setFormData(prev => ({
      ...prev,
      galleryImages: prev.galleryImages.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.city || !formData.state) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      
      // Create a clean data object without auto-managed fields
      const { id: _, createdAt, updatedAt, viewCount, createdBy, updatedBy, ...cleanData } = formData;
      
      const data = {
        ...cleanData,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        priority: parseInt(formData.priority) || 0
      };

      let response;
      if (isEditMode) {
        response = await ApiService.updateTirthSthal(id, data);
        toast.success('Tirth Sthal updated successfully');
      } else {
        response = await ApiService.createTirthSthal(data);
        toast.success('Tirth Sthal created successfully');
      }

      if (response.success) {
        navigate('/tirth-sthals');
      }
    } catch (error) {
      console.error('Error saving Tirth Sthal:', error);
      toast.error(error.message || 'Failed to save Tirth Sthal');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/tirth-sthals')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
        >
          <ArrowLeft size={20} />
          Back to Tirth Sthals
        </button>
        <h1 className="text-3xl font-bold text-gray-800">
          {isEditMode ? 'Edit Tirth Sthal' : 'Add New Tirth Sthal'}
        </h1>
      </div>

      {loading && !formData.name ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Enter Tirth Sthal name"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <AutoResizeTextarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="4"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Enter detailed description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Deity/Associated God
                </label>
                <input
                  type="text"
                  name="deity"
                  value={formData.deity}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="e.g., Lord Shiva, Goddess Durga"
                />
              </div>
            </div>
          </div>

          {/* Location Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <MapPin size={20} />
              Location Information
            </h2>

            {/* Google Places Search */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Location (Google Places)
              </label>
              <input
                ref={autocompleteInputRef}
                type="text"
                placeholder="Search for temples, places, cities..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                {GOOGLE_MAPS_API_KEY 
                  ? 'Start typing to search and select a place to auto-fill location details including coordinates'
                  : '⚠️ Google Maps API key not configured. Add VITE_GOOGLE_MAPS_API_KEY to your .env file'
                }
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Street address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  State <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country
                </label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Latitude (for maps)
                </label>
                <input
                  type="number"
                  step="any"
                  name="latitude"
                  value={formData.latitude}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="e.g., 28.6139"
                />
              </div> */}

              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Longitude (for maps)
                </label>
                <input
                  type="number"
                  step="any"
                  name="longitude"
                  value={formData.longitude}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="e.g., 77.2090"
                />
              </div> */}
            </div>
          </div>

          {/* Media */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Image size={20} />
              Media
            </h2>
            
            {/* Main Image */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Main Image
              </label>
              {formData.imageUrl && (
                <img
                  src={formData.imageUrl}
                  alt="Preview"
                  className="w-48 h-48 object-cover rounded-lg mb-2"
                />
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload(e, 'imageUrl')}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
              />
            </div>

            {/* Video URL */}
            {/* <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Video URL (YouTube/Vimeo)
              </label>
              <input
                type="url"
                name="videoUrl"
                value={formData.videoUrl}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="https://youtube.com/..."
              />
            </div> */}

            {/* Gallery Images */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gallery Images
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                {formData.galleryImages.map((img, index) => (
                  <div key={index} className="relative">
                    <img
                      src={img}
                      alt={`Gallery ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveGalleryImage(index)}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload(e, 'galleryImages')}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
              />
            </div>
          </div>

          {/* Visiting Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Visiting Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Best Time to Visit
                </label>
                <input
                  type="text"
                  name="bestTimeToVisit"
                  value={formData.bestTimeToVisit}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="e.g., October to March"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Darshan Timings
                </label>
                <input
                  type="text"
                  name="darshantimings"
                  value={formData.darshantimings}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="e.g., 6:00 AM - 8:00 PM"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Entry Fee
                </label>
                <input
                  type="text"
                  name="entryFee"
                  value={formData.entryFee}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="e.g., Free / ₹50 per person"
                />
              </div>
            </div>
          </div>

          {/* How to Reach */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">How to Reach</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nearest Airport
                </label>
                <input
                  type="text"
                  name="nearestAirport"
                  value={formData.nearestAirport}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nearest Railway Station
                </label>
                <input
                  type="text"
                  name="nearestRailway"
                  value={formData.nearestRailway}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Detailed Transportation Instructions
                </label>
                <AutoResizeTextarea
                  name="howToReach"
                  value={formData.howToReach}
                  onChange={handleInputChange}
                  rows="4"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Describe how to reach this place by various modes of transport"
                />
              </div>
            </div>
          </div>

          {/* Additional Details */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Additional Details</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Significance
                </label>
                <AutoResizeTextarea
                  name="significance"
                  value={formData.significance}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Religious/spiritual significance"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  History
                </label>
                <AutoResizeTextarea
                  name="history"
                  value={formData.history}
                  onChange={handleInputChange}
                  rows="4"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Historical background"
                />
              </div>

              {/* Array Fields */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nearby Places
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={tempInput.nearbyPlace}
                    onChange={(e) => setTempInput(prev => ({ ...prev, nearbyPlace: e.target.value }))}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Enter nearby place"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddArrayItem('nearbyPlaces', 'nearbyPlace'))}
                  />
                  <button
                    type="button"
                    onClick={() => handleAddArrayItem('nearbyPlaces', 'nearbyPlace')}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                  >
                    <Plus size={20} />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.nearbyPlaces.map((place, index) => (
                    <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full flex items-center gap-2">
                      {place}
                      <button
                        type="button"
                        onClick={() => handleRemoveArrayItem('nearbyPlaces', index)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <X size={16} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Festivals Celebrated
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={tempInput.festival}
                    onChange={(e) => setTempInput(prev => ({ ...prev, festival: e.target.value }))}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Enter festival name"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddArrayItem('festivals', 'festival'))}
                  />
                  <button
                    type="button"
                    onClick={() => handleAddArrayItem('festivals', 'festival')}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                  >
                    <Plus size={20} />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.festivals.map((festival, index) => (
                    <span key={index} className="px-3 py-1 bg-green-100 text-green-800 rounded-full flex items-center gap-2">
                      {festival}
                      <button
                        type="button"
                        onClick={() => handleRemoveArrayItem('festivals', index)}
                        className="text-green-600 hover:text-green-800"
                      >
                        <X size={16} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rituals
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={tempInput.ritual}
                    onChange={(e) => setTempInput(prev => ({ ...prev, ritual: e.target.value }))}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Enter ritual"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddArrayItem('rituals', 'ritual'))}
                  />
                  <button
                    type="button"
                    onClick={() => handleAddArrayItem('rituals', 'ritual')}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                  >
                    <Plus size={20} />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.rituals.map((ritual, index) => (
                    <span key={index} className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full flex items-center gap-2">
                      {ritual}
                      <button
                        type="button"
                        onClick={() => handleRemoveArrayItem('rituals', index)}
                        className="text-purple-600 hover:text-purple-800"
                      >
                        <X size={16} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Facilities Available
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={tempInput.facility}
                    onChange={(e) => setTempInput(prev => ({ ...prev, facility: e.target.value }))}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Enter facility (e.g., Parking, Accommodation)"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddArrayItem('facilities', 'facility'))}
                  />
                  <button
                    type="button"
                    onClick={() => handleAddArrayItem('facilities', 'facility')}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                  >
                    <Plus size={20} />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.facilities.map((facility, index) => (
                    <span key={index} className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full flex items-center gap-2">
                      {facility}
                      <button
                        type="button"
                        onClick={() => handleRemoveArrayItem('facilities', index)}
                        className="text-yellow-600 hover:text-yellow-800"
                      >
                        <X size={16} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Settings */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Settings</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    className="rounded text-orange-600 focus:ring-orange-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Active</span>
                </label>
              </div>

              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="isFeatured"
                    checked={formData.isFeatured}
                    onChange={handleInputChange}
                    className="rounded text-orange-600 focus:ring-orange-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Featured</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority (0-100)
                </label>
                <input
                  type="number"
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/tirth-sthals')}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || uploading}
              className="flex items-center gap-2 px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
            >
              <Save size={20} />
              {loading ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AddEditTirthSthal;
